import { Capacitor } from '@capacitor/core'
import { Keyboard } from '@capacitor/keyboard'
import { UntitledIcon, type UntitledIconData } from '@faceup/icons'
import { twMerge, UnstyledButton } from '@faceup/ui'
import { useElementSize } from '@mantine/hooks'
import { type ReactNode, useEffect, useState } from 'react'

type MobileFooterShellProps = {
  children: ReactNode
  itemCount: number
}

export const MobileFooterShell = ({ children, itemCount }: MobileFooterShellProps) => {
  const { ref, height } = useElementSize()
  const [isHidden, setIsHidden] = useState(false)

  useEffect(() => {
    // The app client is detected from the user agent, so this also renders in a
    // plain browser, where the Keyboard plugin has no web implementation.
    if (!Capacitor.isPluginAvailable('Keyboard')) {
      return
    }

    const showHandle = Keyboard.addListener('keyboardWillShow', () => setIsHidden(true))
    const hideHandle = Keyboard.addListener('keyboardWillHide', () => setIsHidden(false))

    return () => {
      showHandle.then(h => h.remove())
      hideHandle.then(h => h.remove())
    }
  }, [])

  if (isHidden) {
    return null
  }

  return (
    <div
      className='fixed bottom-0 w-full border-t-1 border-t-[#E6ECF9] bg-white'
      style={{ height }}
    >
      <div
        ref={ref}
        className='grid w-full justify-between pb-[env(safe-area-inset-bottom)]'
        style={{
          gridTemplateColumns: `repeat(${itemCount}, minmax(0, 1fr))`,
        }}
      >
        {children}
      </div>
    </div>
  )
}

type ButtonItemProps = {
  onClick: () => void
  active: boolean
  icon: UntitledIconData
  children: ReactNode
}

export const ButtonItem = ({ icon, onClick, active, children }: ButtonItemProps) => (
  <UnstyledButton onClick={onClick}>
    <div
      className={twMerge(
        'flex flex-col gap-1 overflow-hidden whitespace-nowrap px-1 py-3 text-center',
        active && 'text-colorPrimary'
      )}
    >
      <UntitledIcon icon={icon} size={20} />
      <span className='overflow-hidden text-ellipsis'>{children}</span>
    </div>
  </UnstyledButton>
)
